import { useRef, useState } from 'react'
import { FileSpreadsheet, Upload } from 'lucide-react'
import api from '../services/api'
import LookupSelect from '../components/LookupSelect'
import { useEmpresa } from '../contexts/EmpresaContext'

// Importar Despesas dos Funcionários (WMOTLOGX/PMOTLOGX): confere a planilha antes e só grava depois

interface Item { linha: number; ident: string; valor: number; motcod: string; nome: string; situacao: string; fatura: boolean; numFat?: number }
interface Resultado { compe: string; dataLanc: string; conta: { placod: number; plades: string }; tipOpe: number; par: { doc: string; cta: string }; motivoSemFinanceiro: string; itens: Item[] }

const hojeIso = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}` }
const br = (iso: string) => iso.split('-').reverse().join('/')
const moeda = (v: number) => v.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

export default function ImportarDespesasFuncionarios() {
  const { empresaCod } = useEmpresa()
  const hoje = new Date()
  const [ano, setAno] = useState(String(hoje.getFullYear()))
  const [mes, setMes] = useState(String(hoje.getMonth() + 1))
  const [placod, setPlacod] = useState('')
  const [plades, setPlades] = useState('')
  const [emissao, setEmissao] = useState(hojeIso())
  const [vencimento, setVencimento] = useState(hojeIso())
  const [numeroFatura, setNumeroFatura] = useState('')
  const [arquivo, setArquivo] = useState<{ nome: string; base64: string } | null>(null)
  const [previa, setPrevia] = useState<Resultado | null>(null)
  const [gravado, setGravado] = useState(false)
  const [erro, setErro] = useState('')
  const [ocupado, setOcupado] = useState<'' | 'conferindo' | 'importando'>('')
  const arqRef = useRef<HTMLInputElement>(null)

  function escolher(f: File | undefined) {
    if (!f) return
    const r = new FileReader()
    r.onload = () => { setArquivo({ nome: f.name, base64: String(r.result).split(',')[1] ?? '' }); setPrevia(null); setGravado(false); setErro('') }
    r.readAsDataURL(f)
  }

  const corpo = () => ({
    empcod: empresaCod ?? 1, ano: Number(ano), mes: Number(mes), placod: Number(placod), emissao, vencimento,
    numeroFatura: Number(numeroFatura) || 0, arquivo: arquivo?.base64 ?? '', nomeArquivo: arquivo?.nome ?? '',
  })

  async function conferir() {
    setErro(''); setGravado(false); setPrevia(null)
    if (!ano || !mes) return setErro('Data Inválida')
    if (!placod) return setErro('Plano de Contas Inválido')
    if (!arquivo) return setErro('Selecione o arquivo a ser importado!')
    setOcupado('conferindo')
    try { setPrevia((await api.post('/importar-despesas-funcionarios/previa', corpo())).data) }
    catch (e: any) { setErro(e.response?.data?.error ?? 'Erro ao ler a planilha.') }
    finally { setOcupado('') }
  }

  async function importar() {
    if (!previa) return
    const qtd = previa.itens.filter(i => i.motcod).length, fats = previa.itens.filter(i => i.fatura).length
    if (!window.confirm(`Importar ${qtd} despesa(s) na conta ${previa.conta.placod} - ${previa.conta.plades}${fats ? ` e gerar ${fats} fatura(s) a pagar` : ''}?`)) return
    setErro(''); setOcupado('importando')
    try { setPrevia((await api.post('/importar-despesas-funcionarios', corpo())).data); setGravado(true) }
    catch (e: any) { setErro(e.response?.data?.error ?? 'Erro ao importar.') }
    finally { setOcupado('') }
  }

  const th: React.CSSProperties = { padding: '0.45rem 0.55rem', fontSize: '0.68rem', fontWeight: 800, color: '#fff', background: '#1b3f7b', textAlign: 'left', whiteSpace: 'nowrap', textTransform: 'uppercase' }
  const td: React.CSSProperties = { padding: '0.4rem 0.55rem', fontSize: '0.82rem', borderBottom: '1px solid #eef2f7', whiteSpace: 'nowrap' }
  const total = previa?.itens.filter(i => i.motcod).reduce((s, i) => s + i.valor, 0) ?? 0
  const naoAchados = previa?.itens.filter(i => !i.motcod).length ?? 0

  return (
    <div style={{ width: '100%', maxWidth: 1100 }}>
      <h2 style={{ marginTop: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
        Importar Despesas dos Funcionários
        <span style={{ color: '#9ca3af', fontFamily: 'monospace', fontSize: '0.75rem', fontWeight: 400 }} title="PMOTLOGX">WMOTLOGX</span>
      </h2>
      <div className="card">
        <div style={{ display: 'flex', gap: '0.75rem 1rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div className="field">
            <label>Ano / Mês <span style={{ fontWeight: 400, textTransform: 'none', color: '#6b7280' }}>(processamento)</span></label>
            <div style={{ display: 'flex', gap: 6 }}>
              <input value={ano} maxLength={4} inputMode="numeric" style={{ width: 70 }} onChange={e => setAno(e.target.value.replace(/\D/g, ''))} />
              <input value={mes} maxLength={2} inputMode="numeric" style={{ width: 50 }} onChange={e => setMes(e.target.value.replace(/\D/g, ''))} />
            </div>
          </div>
          <div className="field" style={{ minWidth: 340 }}>
            <label>Conta <span style={{ fontWeight: 400, textTransform: 'none', color: '#6b7280' }}>(Adiantamento/Salário)</span></label>
            <LookupSelect endpoint="/erp/planos" codKey="placod" descKey="plades" tagKey="placod" codValue={placod} descValue={plades}
              onSelect={(c, d) => { setPlacod(c); setPlades(d); setPrevia(null) }} placeholder="Buscar conta..." listWidth={360} />
          </div>
          <div className="field">
            <label>Data Emissão</label>
            <input type="date" value={emissao} onChange={e => setEmissao(e.target.value)} />
          </div>
          <div className="field">
            <label>Vencimento</label>
            <input type="date" value={vencimento} onChange={e => setVencimento(e.target.value)} />
          </div>
          <div className="field">
            <label>Número Fatura <span style={{ fontWeight: 400, textTransform: 'none', color: '#6b7280' }}>(p/ financeiro)</span></label>
            <input value={numeroFatura} inputMode="numeric" maxLength={9} placeholder="Sequencial" style={{ width: 130 }}
              onChange={e => setNumeroFatura(e.target.value.replace(/\D/g, ''))} />
          </div>
        </div>

        <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', marginTop: '1rem', flexWrap: 'wrap' }}>
          <input ref={arqRef} type="file" accept=".xlsx,.csv" style={{ display: 'none' }} onChange={e => { escolher(e.target.files?.[0]); e.target.value = '' }} />
          <button className="btn" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: '#217346', color: '#fff', border: '1px solid #1a5c38' }} onClick={() => arqRef.current?.click()}>
            <FileSpreadsheet size={16} /> {arquivo ? 'Trocar planilha' : 'Selecionar planilha'}
          </button>
          <span style={{ fontSize: '0.85rem', color: arquivo ? '#1f2937' : '#9ca3af', fontWeight: arquivo ? 600 : 400 }}>{arquivo?.nome ?? 'Nenhum arquivo selecionado (.xlsx ou .csv)'}</span>
          <div style={{ flex: 1 }} />
          <button className="btn btn-primary" onClick={conferir} disabled={!!ocupado}>{ocupado === 'conferindo' ? 'Conferindo...' : 'Conferir planilha'}</button>
        </div>
        <div style={{ fontSize: '0.74rem', color: '#6b7280', marginTop: 8 }}>
          Planilha: célula A1 com a competência (01/MM/AAAA); da linha 2 em diante, coluna A = identidade ou CPF do funcionário e coluna B = valor.
        </div>

        {erro && <div style={{ marginTop: '0.9rem', fontSize: '0.84rem', fontWeight: 600, padding: '0.55rem 0.9rem', borderRadius: 8, background: '#fee2e2', color: '#991b1b', border: '1px solid #fecaca' }}>{erro}</div>}

        {previa && (
          <div style={{ marginTop: '1rem' }}>
            <div style={{ display: 'flex', gap: '1.2rem', flexWrap: 'wrap', alignItems: 'center', padding: '0.65rem 0.9rem', borderRadius: 8,
              background: gravado ? '#dcfce7' : '#eff6ff', border: `1px solid ${gravado ? '#86efac' : '#bfdbfe'}`, fontSize: '0.84rem' }}>
              <b style={{ color: gravado ? '#15803d' : '#1b3f7b' }}>{gravado ? 'Importação concluída' : 'Conferência (nada gravado ainda)'}</b>
              <span>Competência <b>{previa.compe}</b></span>
              <span>Lançamento em <b>{br(previa.dataLanc)}</b></span>
              <span>Conta <b>{previa.conta.placod} - {previa.conta.plades}</b></span>
              <span>Total <b>R$ {moeda(total)}</b></span>
              {naoAchados > 0 && <span style={{ color: '#b91c1c', fontWeight: 700 }}>{naoAchados} não encontrado(s)</span>}
            </div>
            <div style={{ fontSize: '0.76rem', color: previa.motivoSemFinanceiro ? '#b45309' : '#6b7280', margin: '0.45rem 0 0.7rem' }}>
              {previa.motivoSemFinanceiro
                ? `Financeiro: não gera faturas — ${previa.motivoSemFinanceiro}.`
                : `Financeiro: documento ${previa.par.doc}, conta financeira ${previa.par.cta} (operação ${previa.tipOpe} - ${previa.tipOpe === 8 ? 'Adiantamentos' : 'Salários'}); só para funcionários cadastrados como cliente/fornecedor.`}
            </div>
            <div style={{ border: '1px solid #bfdbfe', borderRadius: 8, overflow: 'hidden' }}>
              <table style={{ width: '100%', maxWidth: 'none', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={th}>Linha</th><th style={th}>Identificação</th><th style={th}>Funcionário</th>
                    <th style={{ ...th, textAlign: 'right' }}>Valor</th><th style={th}>Situação</th>{gravado && <th style={th}>Fatura</th>}
                  </tr>
                </thead>
                <tbody>
                  {previa.itens.map((i, k) => (
                    <tr key={i.linha} style={{ background: !i.motcod ? '#fef2f2' : k % 2 ? '#f5f9fd' : '#fff' }}>
                      <td style={td}>{i.linha}</td>
                      <td style={td}>{i.ident}</td>
                      <td style={{ ...td, fontWeight: 600 }}>{i.motcod ? `${i.motcod} - ${i.nome}` : '—'}</td>
                      <td style={{ ...td, textAlign: 'right' }}>{moeda(i.valor)}</td>
                      <td style={{ ...td, color: !i.motcod ? '#b91c1c' : i.fatura ? '#15803d' : '#b45309', fontWeight: 600 }}>{i.situacao}</td>
                      {gravado && <td style={td}>{i.numFat ? `${previa.par.doc} ${i.numFat}` : ''}</td>}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {!gravado && (
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '0.9rem' }}>
                <button className="btn btn-primary" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }} onClick={importar}
                  disabled={!!ocupado || !previa.itens.some(i => i.motcod)}>
                  <Upload size={15} /> {ocupado === 'importando' ? 'Importando...' : 'Importar'}
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
